import { DataSubmission } from "psims/models/data-submission";
import useFocusedField, { UseFocusedField } from "psims/react/util/use-focused-field";
import useViewMode, { ViewMode } from "../../shared/use-view-mode";
import { CommentsFocusField, DeclarationFocusField } from "../../shared/types";
import useConfirm from "../../shared/use-confirm";
import useSubmit from "../../shared/use-submit";
import { StockholdingFocusField, OwnershipFocusField, StockOnBehalfFocusField, MsoFocusField, StockRefineryHeldFocusField, DeleteFocusField } from "../shared/types";
import useMsoRefData from "../shared/use-mso-ref-data";
import { UseMsoRefinerAPI } from "./use-mso-refiner-api";
import useMsoRefinerControls from "./use-mso-refiner-controls";
import useMsoRefinerForm from "./use-mso-refiner-form";
import useMsoRefinerNavigation from "./use-mso-refiner-navigation";
import useMsoRefinerProgress from "./use-mso-refiner-progress";
import useMsoRefinerValidation from "./use-mso-refiner-validation";
import useMsoRefinerValidationAlerts from "./use-mso-refiner-validation-alerts";
import useMsoRefinerServiceMessages from "./use-mso-refiner-service-messages";
import useClearData from "psims/react/pages/primary-pages/data-submissions/shared/use-clear-data";
import usePortalData from "../../shared/use-portal-data";
import { useMemo } from "react";
import { MsoStockholding } from "psims/models/submission-types/mso/mso-stockholding";
import { MsoStockOnBehalfVM } from "psims/gen/xapi-client";

interface UseMsoRefinerProps {
    apiCtrl: PopulatedProps<UseMsoRefinerAPI, 'submission'>;
    prevApiCtrl: PopulatedProps<UseMsoRefinerAPI, 'submission'>;
}

type FocusField = StockholdingFocusField |
                  OwnershipFocusField |
                  StockOnBehalfFocusField |
                  StockRefineryHeldFocusField |
                  MsoFocusField |
                  CommentsFocusField |
                  DeclarationFocusField |
                  DeleteFocusField;

export type UseMsoRefinerFocus = UseFocusedField<FocusField>;

function useMsoRefiner({apiCtrl, prevApiCtrl}: UseMsoRefinerProps) {
    const {submission} = apiCtrl;

    const mergedSubmission = useMemo(() => {
            if (!prevApiCtrl) return submission;
            const status = submission.dataSubmission.status;
            
            if (status !== "Not started") {
            return submission;
            }
            
            function mergePrevStockholdings (
                current: MsoStockholding[],
                prev: MsoStockholding[],
                dueDate: string, //value is obligation date
                newDataSubmissionId: number
            ): MsoStockholding[] {
                const dueTime = new Date(dueDate).getTime();
                // Start with current stockholdings
                const merged: MsoStockholding[] = [...current];
    
                prev.forEach(prevStock => {
                    const existing = merged.find(c => c.msoProductId === prevStock.msoProductId);
    
                    const carriedOwnerships =
                        prevStock.stockholdingOwnerships?.filter(o =>
                            !o.endDate || new Date(o.endDate).getTime() >= dueTime
                        ) ?? [];
                        
                        if (existing) {
                        if (!carriedOwnerships.length) return; // nothing to merge
    
                        existing.stockholdingOwnerships = [
                            ...(existing.stockholdingOwnerships ?? []),
                            ...carriedOwnerships.map(o => ({
                            ...o,
                            id: undefined,
                            msoStockholdingId: undefined,
                            dataSubmissionId: newDataSubmissionId,
                            
                            })),
                        ]; 
                        return;
                        }
    
                if (!carriedOwnerships.length) return;        // Stockholding does not exist → insert new one with carried ownerships
                merged.push({
                ...prevStock,
                id: undefined!,
                concurrencyToken: undefined!, // reset concurrency token if needed
                dataSubmissionId: newDataSubmissionId,
                density:undefined,
                holderOwnerStockVolume:undefined,
                pipeline:undefined,
                eez:undefined,
                stockholdingOwnerships: carriedOwnerships.map(o => ({
                    ...o,
                    id: undefined,
                    msoStockholdingId: undefined,
                    dataSubmissionId: newDataSubmissionId,
                    clientId: crypto.randomUUID(),
                    concurrencyToken: undefined!,
                    msoProductId:prevStock.msoProductId,
                })),
                });
            });
    
                return merged;
            }
    
            //merge stockOnBehalf
    
            function mergePrevStocksOnBehalf(
                current: MsoStockOnBehalfVM[], // replace with proper type if available
                prev: MsoStockOnBehalfVM[],
                dueDate: string, //value is obligation date
                newDataSubmissionId: number
            ) {
                const dueTime = new Date(dueDate).getTime();
    
                const merged = [...(current ?? [])];
    
                prev.forEach(prevStock => {
                    // carry forward only active ones
                    const isActive =
                        !prevStock.endDate ||
                        new Date(prevStock.endDate).getTime() >= dueTime;
    
                    if (!isActive) return;
    
                    /* const exists = merged.find(
                        c =>
                            c.msoProductId === prevStock.msoProductId
                            // && c.entityName === prevStock.entityName
                    );
    
                    if (exists) return; */
    
                    merged.push({
                        ...prevStock,
                        id: undefined!,
                        dataSubmissionId: newDataSubmissionId,
                        concurrencyToken: undefined!,
                        clientId: crypto.randomUUID()!,
                    });
                });
    
                return merged;
            }
    
    
            // final return mergedSubmission
            return{
                ...submission,
                stockholdings: mergePrevStockholdings(
                submission.stockholdings ?? [],
                prevApiCtrl?.submission?.stockholdings ?? [],
                submission.dataSubmission.obligationDate!,
                submission.dataSubmission.id
                ),
                stocksOnBehalf: mergePrevStocksOnBehalf(
                submission.stocksOnBehalf ?? [],
                prevApiCtrl?.submission?.stocksOnBehalf ?? [],
                submission.dataSubmission.obligationDate!,
                submission.dataSubmission.id
                ),
            };
         },[submission, prevApiCtrl]);
    

    const refData = useMsoRefData({dataSubmission: mergedSubmission.dataSubmission});

    const portalDataCtrl = usePortalData({submissionType: refData.submissionType});

    const viewMode = useViewMode(submission);

    const progressCtrl = useMsoRefinerProgress({apiCtrl, viewMode});

    const focusFieldCtrl = useFocusedField<FocusField>();

    const formCtrl = useMsoRefinerForm({refData, mergedSubmission});

    const validationCtrl = useMsoRefinerValidation({focusFieldCtrl, formCtrl, progressCtrl, refData});

    const submitCtrl = useSubmit({
        apiCtrl,
        dataSubmission: submission.dataSubmission as DataSubmission<'MSO refiner'>,
        focusFieldCtrl,
        hasExternalErrors: validationCtrl.notifications.length > 0,
    });

    const validationAlertsCtrl = useMsoRefinerValidationAlerts({focusFieldCtrl, msoCtrl: formCtrl.msoCtrl, progressCtrl});
    
    const deleteCtrl = useConfirm();

    const navCtrl = useMsoRefinerNavigation({
        apiCtrl,
        formCtrl,
        msoCtrl: formCtrl.msoCtrl,
        progressCtrl,
        submitCtrl,
        validationCtrl,
        viewMode
    });

    const controlsCtrl = useMsoRefinerControls({apiCtrl, navCtrl, progressCtrl, validationCtrl, viewMode});

    const serviceMessages = useMsoRefinerServiceMessages({
        formCtrl,
        progressCtrl,
        serverError: apiCtrl.updateError,
        submissionStatus: apiCtrl.loadStatus,
        submitCtrl,
        updateResponse: apiCtrl.updateResponse
    });

    const clearDataCtrl = useClearData({
        onConfirm: () => {
            apiCtrl.clearAllData();
            submitCtrl.updateDeclaration(false);
        },
        submission: apiCtrl.submission?.dataSubmission,
    });

    const isDisabled = viewMode !== 'edit';

    return {
        apiCtrl,
        clearDataCtrl,
        controlsCtrl,
        deleteCtrl,
        focusFieldCtrl,
        formCtrl,
        isDisabled,
        navCtrl,
        portalDataCtrl,
        progressCtrl,
        refData,
        serviceMessages,
        submitCtrl,
        validationCtrl,
        validationAlertsCtrl,
        viewMode: viewMode as ViewMode
    };
}

export default useMsoRefiner
